/**
 * Role Normalization Utilities
 *
 * Single source of truth for role normalization and validation.
 * Handles missing roles, legacy formats, and case variations.
 *
 * Contract: CANONICAL_TEAM_CONTRACT_V1 (Role Migration)
 * - Supports dual-field migration: teamRole (v1.2) and role (legacy)
 * - Normalizes case variations (Owner → owner, ADMIN → admin)
 * - Provides safe defaults for missing/invalid roles
 */

import type { TeamRole } from './teamAuthz';

/**
 * Valid team roles (canonical set)
 */
const VALID_ROLES: readonly TeamRole[] = ['owner', 'manager', 'dealer', 'mechanic'] as const;

/**
 * Legacy role aliases (for backward compatibility)
 */
const LEGACY_ROLE_MAP: Record<string, TeamRole> = {
  admin: 'manager', // Legacy admin → manager
  member: 'mechanic', // Legacy member → mechanic
  viewer: 'mechanic', // Legacy viewer → mechanic (read-only not implemented yet)
};

/**
 * Type guard: check if a value is a valid TeamRole
 */
export function isValidRole(value: unknown): value is TeamRole {
  if (typeof value !== 'string') return false;
  const normalized = value.toLowerCase();
  return (VALID_ROLES as readonly string[]).includes(normalized) || normalized in LEGACY_ROLE_MAP;
}

/**
 * Normalize a role value to a valid TeamRole
 *
 * Handles:
 * - undefined/null → 'mechanic' (safe default)
 * - Case variations (Owner → owner, ADMIN → admin)
 * - Legacy aliases (admin → manager, member → mechanic)
 * - Invalid values → 'mechanic' (with warning in dev)
 *
 * @param value - Raw role value (may be undefined, null, or invalid)
 * @param context - Optional context for logging (e.g., 'UserSelector', 'member.uid')
 * @returns Valid TeamRole (never undefined)
 */
export function normalizeRole(value: unknown, context?: string): TeamRole {
  // Handle missing values
  if (value === undefined || value === null || value === '') {
    // Development warning disabled for production build
    return 'mechanic';
  }

  // Handle non-string values
  if (typeof value !== 'string') {
    // Development warning disabled for production build
    return 'mechanic';
  }

  // Normalize to lowercase
  const normalized = value.toLowerCase();

  // Check if it's a valid canonical role
  if ((VALID_ROLES as readonly string[]).includes(normalized)) {
    return normalized as TeamRole;
  }

  // Check if it's a legacy alias
  if (normalized in LEGACY_ROLE_MAP) {
    return LEGACY_ROLE_MAP[normalized];
  }

  // Unknown role - return safe default (development warning disabled for production build)
  return 'mechanic';
}

/**
 * Resolve role from TeamMember object (handles dual-field migration)
 *
 * Resolution order:
 * 1. teamRole (v1.2 canonical field)
 * 2. role (legacy field)
 * 3. 'mechanic' (safe default)
 *
 * @param member - Team member object with role/teamRole fields
 * @param context - Optional context for logging
 * @returns Valid TeamRole (never undefined)
 */
export function resolveTeamMemberRole(
  member: { teamRole?: unknown; role?: unknown; uid?: string },
  context?: string
): TeamRole {
  const memberContext = context || member.uid || 'unknown member';

  // Prefer teamRole (v1.2 canonical)
  if (member.teamRole !== undefined) {
    return normalizeRole(member.teamRole, `${memberContext}.teamRole`);
  }

  // Fallback to role (legacy)
  if (member.role !== undefined) {
    return normalizeRole(member.role, `${memberContext}.role`);
  }

  // No role field at all
  return normalizeRole(undefined, memberContext);
}
